import { takeLatest, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';

// Services
import { transportCardsPlaceApi } from 'services/apiDigitalTicket/transportCardsPlace';

// Actions
import {
  setOpenLoading,
  setClose,
  setOpenDialogError,
} from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import PATH from 'constants/basePath';
import { PLACE_WITH_CARDS_ERROR } from 'constants/transportCardsPlace';

import * as TransportCardsPlaceActions from './actions';
import { TransportCardsPlaceTypes, TransportCardPlaceInterface } from './types';

function* list({
  payload,
}: ReturnType<
  typeof TransportCardsPlaceActions.listTransportCardsPlaceRequest
>): any {
  try {
    yield put(setOpenLoading({ title: 'Carregando locais' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(transportCardsPlaceApi.list, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TransportCardsPlaceActions.listTransportCardsPlaceSuccess({
          transportCardsPlaceList: response.data.map(
            (place: TransportCardPlaceInterface) => ({
              ...place,
              address: Array.isArray(place.Addresses)
                ? place.Addresses[0].street
                : '',
              responsible: place.TransportSupervisor?.name,
            }),
          ),
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error(
      'transportCardsPlace - listTransportCardsPlace - error',
      error,
    );
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TransportCardsPlaceActions.listTransportCardsPlaceFailure());
    yield put(setClose());
  }
}

function* get({
  payload,
}: ReturnType<
  typeof TransportCardsPlaceActions.getTransportCardsPlaceRequest
>): any {
  try {
    yield put(setOpenLoading({ title: 'Carregando locais' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(transportCardsPlaceApi.list, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TransportCardsPlaceActions.getTransportCardsPlaceSuccess({
          transportCardsPlaceData: response.data,
        }),
      );
      yield put(setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error(
      'transportCardsPlace - getTransportCardsPlace - error',
      error,
    );
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TransportCardsPlaceActions.getTransportCardsPlaceFailure());
    yield put(setClose());
  }
}

function* filter({
  payload,
}: ReturnType<
  typeof TransportCardsPlaceActions.filterTransportCardsPlaceRequest
>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(transportCardsPlaceApi.list, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TransportCardsPlaceActions.filterTransportCardsPlaceSuccess({
          transportCardsPlaceList: response.data.map(
            (place: TransportCardPlaceInterface) => ({
              ...place,
              address: Array.isArray(place.Addresses)
                ? place.Addresses[0].street
                : '',
              responsible: place.TransportSupervisor?.name,
            }),
          ),
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error(
      'transportCardsPlace - filterTransportCardsPlace - error',
      error,
    );
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TransportCardsPlaceActions.filterTransportCardsPlaceFailure());
    yield put(setClose());
  }
}

function* create({
  payload,
}: ReturnType<
  typeof TransportCardsPlaceActions.createTransportCardsPlaceRequest
>): any {
  try {
    yield put(setOpenLoading({ title: 'Criando local' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(transportCardsPlaceApi.create, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TransportCardsPlaceActions.createTransportCardsPlaceSuccess({
          transportCardsPlaceData: response.data,
        }),
      );

      yield put(setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Local criado com sucesso',
        }),
      );

      yield put(push(PATH.TRANSPORT_CARDS.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error(
      'transportCardsPlace - createTransportCardsPlace - error',
      error,
    );
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TransportCardsPlaceActions.createTransportCardsPlaceFailure());
    yield put(setClose());
  }
}

function* update({
  payload,
}: ReturnType<
  typeof TransportCardsPlaceActions.updateTransportCardsPlaceRequest
>): any {
  try {
    yield put(setOpenLoading({ title: 'Atualizando local' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(transportCardsPlaceApi.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(TransportCardsPlaceActions.updateTransportCardsPlaceSuccess());
      yield put(setClose());
      yield put(push(PATH.TRANSPORT_CARDS.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error(
      'transportCardsPlace - updateTransportCardsPlace - error',
      error,
    );
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TransportCardsPlaceActions.updateTransportCardsPlaceFailure());
    yield put(setClose());
  }
}

function* remove({
  payload,
}: ReturnType<
  typeof TransportCardsPlaceActions.deleteTransportCardsPlaceRequest
>): any {
  try {
    yield put(setOpenLoading({ title: 'Deletando local' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(transportCardsPlaceApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(TransportCardsPlaceActions.deleteTransportCardsPlaceSuccess());
      yield put(setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Local excluído com sucesso',
        }),
      );
      yield put(
        TransportCardsPlaceActions.listTransportCardsPlaceRequest({
          _includes: 'transportcard,address,supervisor',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error(
      'transportCardsPlace - deleteTransportCardsPlace - error',
      error,
    );

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(TransportCardsPlaceActions.deleteTransportCardsPlaceFailure());
    yield put(setClose());

    if (error.response.data.internalCode === PLACE_WITH_CARDS_ERROR) {
      yield put(
        setOpenDialogError({
          title: 'Ops, essa ação não pode ser concluída neste momento.',
          message:
            'Primeiro, mova todos os cartões deste local para um novo local antes de deletá-lo.',
        }),
      );
    }
  }
}

// eslint-disable-next-line import/prefer-default-export
export const transportCardsPlaceSagas = [
  takeLatest(TransportCardsPlaceTypes.LIST_TRANSPORT_CARDS_PLACE_REQUEST, list),
  takeLatest(TransportCardsPlaceTypes.GET_TRANSPORT_CARDS_PLACE_REQUEST, get),
  takeLatest(
    TransportCardsPlaceTypes.FILTER_TRANSPORT_CARDS_PLACE_REQUEST,
    filter,
  ),
  takeLatest(
    TransportCardsPlaceTypes.CREATE_TRANSPORT_CARDS_PLACE_REQUEST,
    create,
  ),
  takeLatest(
    TransportCardsPlaceTypes.UPDATE_TRANSPORT_CARDS_PLACE_REQUEST,
    update,
  ),
  takeLatest(
    TransportCardsPlaceTypes.DELETE_TRANSPORT_CARDS_PLACE_REQUEST,
    remove,
  ),
];
